import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { from, Observable, of, switchMap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class TokenStorageService {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly TOKEN_EXPIRY_KEY = 'token_expiry';
  private readonly isNative = Capacitor.isNativePlatform();
  private readonly hasSecureStorage = Capacitor.isPluginAvailable('SecureStoragePlugin');

  // Cache the encryption key to avoid repeated expensive PBKDF2 operations
  private encryptionKeyCache: CryptoKey | null = null;

  // Cache the decrypted token to avoid repeated decryption operations
  private tokenCache: { token: string; expiryTime: number } | null = null;

  constructor() {}

  async encryptToken(token: string): Promise<string> {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(token);
      const key = await this.getEncryptionKey();
      const iv = window.crypto.getRandomValues(new Uint8Array(12));
      const encryptedData = await window.crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        key,
        data
      );

      // Store IV with encrypted data
      const combined = new Uint8Array(iv.length + encryptedData.byteLength);
      combined.set(iv);
      combined.set(new Uint8Array(encryptedData), iv.length);

      return btoa(String.fromCharCode(...combined));
    } catch (error) {
      console.error('Error encrypting token:', error);
      throw new Error('Failed to encrypt token');
    }
  }

  async decryptToken(encryptedToken: string): Promise<string> {
    try {
      const combined = Uint8Array.from(atob(encryptedToken), c => c.charCodeAt(0));
      const iv = combined.slice(0, 12);
      const encryptedData = combined.slice(12);

      const key = await this.getEncryptionKey();
      const decryptedData = await window.crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        key,
        encryptedData
      );
      return new TextDecoder().decode(decryptedData);
    } catch (error) {
      console.error('Error decrypting token:', error);
      throw new Error('Failed to decrypt token');
    }
  }

  private async getEncryptionKey(): Promise<CryptoKey> {
    // Return cached key if available
    if (this.encryptionKeyCache) {
      return this.encryptionKeyCache;
    }

    const encoder = new TextEncoder();
    const keyData = encoder.encode(environment.security.tokenEncryptionKey);

    // First, derive a key using PBKDF2
    const salt = encoder.encode(environment.security.encryptionSalt);
    const baseKey = await window.crypto.subtle.importKey(
      'raw',
      keyData,
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );

    // Derive the actual encryption key
    const derivedKey = await window.crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 100000,
        hash: 'SHA-256'
      },
      baseKey,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );

    // Cache the key for future use
    this.encryptionKeyCache = derivedKey;
    return derivedKey;
  }

  saveToken(token: string): Observable<void> {
    return from(this.encryptToken(token)).pipe(
      map(encryptedToken => {
        // Store encrypted token
        sessionStorage.setItem(this.TOKEN_KEY, encryptedToken);

        // CRITICAL FIX: Parse JWT token to get actual expiry time from 'exp' claim
        // Don't rely on environment setting which may be incorrect
        const expiryTime = this.getExpiryFromJwt(token);
        sessionStorage.setItem(this.TOKEN_EXPIRY_KEY, expiryTime.toString());

        // Also persist to secure storage on native platforms (best-effort)
        void this.secureSetItem(this.TOKEN_KEY, encryptedToken);
        void this.secureSetItem(this.TOKEN_EXPIRY_KEY, expiryTime.toString());

        // Cache the decrypted token to avoid repeated decryption
        this.tokenCache = { token, expiryTime };

        const timeUntilExpiry = Math.round((expiryTime - Date.now()) / 1000);
        console.log('Token saved and cached successfully', {
          expiryTime: new Date(expiryTime).toISOString(),
          timeUntilExpiry: timeUntilExpiry + 's',
          expiresIn: Math.round(timeUntilExpiry / 60) + ' minutes'
        });
      }),
      catchError(error => {
        console.error('Error saving token:', error);
        return of(undefined);
      })
    );
  }

  /**
   * Parse JWT token to extract expiry time from 'exp' claim
   * This ensures we use the actual token expiry, not a hardcoded value
   */
  private getExpiryFromJwt(token: string): number {
    try {
      // JWT format: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.warn('Invalid JWT format, falling back to environment expiry');
        return Date.now() + (environment.security.tokenExpirationTime * 1000);
      }

      // Decode payload (base64url)
      const payload = parts[1];
      // Replace URL-safe base64 chars with standard base64
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      // Add padding if needed
      const padded = base64 + '==='.slice((base64.length + 3) % 4);

      const decoded = JSON.parse(atob(padded));

      if (decoded.exp) {
        // 'exp' is in seconds since epoch, convert to milliseconds
        const expiryMs = decoded.exp * 1000;
        console.log('Parsed JWT expiry:', {
          exp: decoded.exp,
          expiryTime: new Date(expiryMs).toISOString(),
          timeUntilExpiry: Math.round((expiryMs - Date.now()) / 1000) + 's'
        });
        return expiryMs;
      } else {
        console.warn('JWT has no exp claim, falling back to environment expiry');
        return Date.now() + (environment.security.tokenExpirationTime * 1000);
      }
    } catch (error) {
      console.error('Error parsing JWT expiry, falling back to environment expiry:', error);
      return Date.now() + (environment.security.tokenExpirationTime * 1000);
    }
  }

  getToken(): Observable<string | null> {
    return from(this.readStoredToken()).pipe(
      switchMap(stored => {
        if (!stored) {
          this.tokenCache = null; // Clear cache if storage is empty
          return of(null);
        }

        const expiryTimeNum = Number.parseInt(stored.expiryTime);

        // Check if token is expired
        if (Date.now() > expiryTimeNum) {
          this.removeToken();
          return of(null);
        }

        // Return cached token if available and still valid
        if (this.tokenCache && this.tokenCache.expiryTime === expiryTimeNum) {
          return of(this.tokenCache.token);
        }

        // Token not in cache or cache is stale, decrypt it
        return from(this.decryptToken(stored.encryptedToken)).pipe(
          map(decryptedToken => {
            // Cache the decrypted token
            this.tokenCache = { token: decryptedToken, expiryTime: expiryTimeNum };
            return decryptedToken;
          }),
          catchError(error => {
            console.error('Error retrieving token:', error);
            this.removeToken();
            return of(null);
          })
        );
      }),
      catchError(error => {
        console.error('Error retrieving token:', error);
        this.removeToken();
        return of(null);
      })
    );
  }

  removeToken(): void {
    sessionStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.TOKEN_EXPIRY_KEY);
    // Best-effort native secure storage cleanup
    void this.secureRemoveItem(this.TOKEN_KEY);
    void this.secureRemoveItem(this.TOKEN_EXPIRY_KEY);
    // Clear the cache when token is removed
    this.tokenCache = null;
  }

  clear(): void {
    sessionStorage.clear();
    // Best-effort native secure storage cleanup
    void this.secureRemoveItem(this.TOKEN_KEY);
    void this.secureRemoveItem(this.TOKEN_EXPIRY_KEY);
    // Clear all caches when storage is cleared
    this.tokenCache = null;
    this.encryptionKeyCache = null;
  }

  isTokenExpired(): boolean {
    const expiryTime = sessionStorage.getItem(this.TOKEN_EXPIRY_KEY);
    if (!expiryTime) {
      return true;
    }
    return Date.now() > parseInt(expiryTime);
  }

  getTokenExpiryTime(): number | null {
    const expiryTime = sessionStorage.getItem(this.TOKEN_EXPIRY_KEY);
    return expiryTime ? parseInt(expiryTime) : null;
  }

  private async readStoredToken(): Promise<{ encryptedToken: string; expiryTime: string } | null> {
    const encryptedToken = sessionStorage.getItem(this.TOKEN_KEY);
    const expiryTime = sessionStorage.getItem(this.TOKEN_EXPIRY_KEY);

    if (encryptedToken && expiryTime) {
      return { encryptedToken, expiryTime };
    }

    if (!this.canUseSecureStorage()) {
      return null;
    }

    try {
      const [secureToken, secureExpiry] = await Promise.all([
        this.secureGetItem(this.TOKEN_KEY),
        this.secureGetItem(this.TOKEN_EXPIRY_KEY)
      ]);

      if (secureToken && secureExpiry) {
        sessionStorage.setItem(this.TOKEN_KEY, secureToken);
        sessionStorage.setItem(this.TOKEN_EXPIRY_KEY, secureExpiry);
        return { encryptedToken: secureToken, expiryTime: secureExpiry };
      }
    } catch (error) {
      console.error('Error reading token from secure storage:', error);
    }

    return null;
  }

  private async secureSetItem(key: string, value: string): Promise<void> {
    if (!this.canUseSecureStorage()) {
      return;
    }
    try {
      await SecureStoragePlugin.set({ key, value });
    } catch (error) {
      console.warn('Secure storage set failed:', error);
    }
  }

  private async secureGetItem(key: string): Promise<string | null> {
    if (!this.canUseSecureStorage()) {
      return null;
    }
    try {
      const result = await SecureStoragePlugin.get({ key });
      return result?.value ?? null;
    } catch (error) {
      console.warn('Secure storage get failed:', error);
      return null;
    }
  }

  private async secureRemoveItem(key: string): Promise<void> {
    if (!this.canUseSecureStorage()) {
      return;
    }
    try {
      await SecureStoragePlugin.remove({ key });
    } catch (error) {
      console.warn('Secure storage remove failed:', error);
    }
  }

  private canUseSecureStorage(): boolean {
    return this.isNative && this.hasSecureStorage;
  }
}
